// AdminEventsPage.tsx
import React, { useState, useEffect } from "react";
import EventList from "./EventList";
import EventForm from "./EventForm";
import FunctionList from "./FunctionList";
import FunctionForm from "./FunctionForm";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { AdminEvent, AdminFunction, ZoneDto, FunctionCategoryDto } from "./types";
import AdminBackBar from "../../../components/admin/AdminBackBar";
import "./AdminEvent.css";
import { getAuthHeaders } from "../../../api/authHelper";

const AdminEventsPage: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    // -------------------- STATE --------------------
    const [events, setEvents] = useState<AdminEvent[]>([]);
    const [functions, setFunctions] = useState<AdminFunction[]>([]);
    const [zones, setZones] = useState<ZoneDto[]>([]);

    const [selectedEvent, setSelectedEvent] = useState<AdminEvent | null>(null);
    const [selectedFunction, setSelectedFunction] = useState<AdminFunction | null>(null);
    const [selectedEventForFunctions, setSelectedEventForFunctions] = useState<number | null>(null);
    const [categories, setCategories] = useState<FunctionCategoryDto[]>([]);

    // -------------------- SAFE FETCH --------------------
    const fetchJsonSafe = async (url: string, options?: RequestInit) => {
        try {
            const token = sessionStorage.getItem("authToken");

            const res = await fetch(url, {
                ...options,
                headers: {
                    ...(options?.headers || {}),
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            if (!res.ok) {
                const text = await res.text();

                // 🔐 auto logout hvis token er ugyldig
                if (res.status === 401) {
                    sessionStorage.clear();
                    window.location.href = "/login";
                }

                throw new Error(text || `HTTP ${res.status}`);
            }

            const type = res.headers.get("content-type");

            return type?.includes("application/json")
                ? await res.json()
                : null;

        } catch (err) {
            console.error("Fetch error:", err);
            return null;
        }
    };

    // -------------------- HELPERS --------------------
    const normalizeZone = (z: any): ZoneDto => ({
        zoneID: Number(z.zoneID ?? z.ZoneID),
        number: z.number ?? z.Number,
        zoneName: z.zoneName ?? z.ZoneName,
    });

    const getFunctionId = (f: any): number | null =>
        Number(f?.functionID ?? f?.FunctionID ?? f?.functionId) || null;

    // -------------------- FETCH DATA --------------------
    const fetchEvents = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/events`, {
            headers: getAuthHeaders(),
        })
        setEvents(data || []);
    };

    const fetchFunctions = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/function`, {
            headers: getAuthHeaders(),
        })
        if (!data) {
            setFunctions([]);
            return;
        }

        const functionsWithZones: AdminFunction[] = (
            await Promise.all(
                data.map(async (f: any) => {
                    const functionID = getFunctionId(f);
                    if (!functionID) return undefined;

                    const zones = await fetchJsonSafe(
                        `${apiUrl}/api/zones/functions/byfunction/${functionID}`
                    );
                    return {
                        functionID,
                        functionName: f.functionName ?? f.FunctionName ?? "",
                        description: f.description ?? f.Description ?? "",
                        eventID: Number(f.eventID ?? f.EventID),
                        functionCategoryId: Number(
                            f.functionCategoryId ?? f.FunctionCategoryId ?? 0
                        ) || undefined,
                        categoryName: f.categoryName ?? f.CategoryName ?? "",
                        useDesign2: f.useDesign2 ?? f.UseDesign2 ?? false,
                        assignedZones: (zones || []).map(normalizeZone),
                    } as AdminFunction;
                })
            )
        ).filter(Boolean) as AdminFunction[];

        setFunctions(functionsWithZones);
    };

    const fetchZones = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/zones`);
        setZones(data || []);
    };

    const fetchCategories = async () => {
        const data = await fetchJsonSafe(`${apiUrl}/api/functioncategory`, {
            headers: getAuthHeaders(),
        });
        setCategories(data || []);
    };

    useEffect(() => {
        fetchEvents();
        fetchFunctions();
        fetchZones();
        fetchCategories();
    }, []);


    // -------------------- EVENT HANDLERS --------------------
    const handleEditEvent = async (eventID: number) => {
        const event = await fetchJsonSafe(`${apiUrl}/api/events/${eventID}/light`, {
            headers: getAuthHeaders(),
        });
        if (!event) return;

        const images = await fetchJsonSafe(`${apiUrl}/api/events/${eventID}/images`, {
                        headers: getAuthHeaders(),
                    });

        setSelectedEvent({
            ...event,
            eventLogoPath: images?.logo ?? null,
            eventIDCardPath: images?.idCard ?? null,
        });

        setSelectedEventForFunctions(eventID);
        setSelectedFunction(null);
    };

    const handleSaveEvent = async (event: AdminEvent) => {
        const method = event.eventID ? "PUT" : "POST";
        const url = event.eventID
            ? `${apiUrl}/api/events/${event.eventID}`
            : `${apiUrl}/api/events`;

        await fetchJsonSafe(url, {
            method,
            headers: getAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(event),
        });

        await fetchEvents();
        setSelectedEvent(null);
    };

    const handleDeleteEvent = async (eventID: number) => {
        if (!window.confirm("Slet dette event?")) return;

        await fetchJsonSafe(`${apiUrl}/api/events/${eventID}`, {
            method: "DELETE",
            headers: getAuthHeaders(), // 🔥 vigtig
        });

        await fetchEvents();
        await fetchFunctions();
        setSelectedEvent(null);
        setSelectedEventForFunctions(null);
    };

    // -------------------- FUNCTION HANDLERS --------------------
    const handleSaveFunction = async (func: AdminFunction & { assignedZones?: ZoneDto[] }) => {
        if (!func.eventID) {
            alert("Vælg et event først!");
            return;
        }

        if (!func.functionCategoryId) {
            alert("Vælg en kategori!");
            return;
        }

        const payload = {
            FunctionName: func.functionName,
            Description: func.description,
            EventID: func.eventID,
            FunctionCategoryId: func.functionCategoryId,
            UseDesign2: func.useDesign2 ?? false,
        };

        let functionID: number | null = null;

        if (func.functionID) {
            const updated = await fetchJsonSafe(`${apiUrl}/api/function/${func.functionID}`, {
                method: "PUT",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    ...payload,
                    FunctionId: func.functionID
                }),
            });
            functionID = getFunctionId(updated) ?? func.functionID;
        } else {
            const created = await fetchJsonSafe(`${apiUrl}/api/function`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });
            functionID = getFunctionId(created);
        }

        if (!functionID) {
            console.error("Could not resolve functionID");
            return;
        }

        await fetchJsonSafe(`${apiUrl}/api/zones/functions/removeall/${functionID}`, {
            method: "DELETE"
        });

        if (func.assignedZones?.length) {
            await Promise.all(
                func.assignedZones.map(zone =>
                    fetchJsonSafe(`${apiUrl}/api/zones/functions`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ FunctionId: functionID, ZoneID: zone.zoneID }),
                    })
                )
            );
        }

        await fetchFunctions();
        setSelectedFunction(null);
    };

    const handleDeleteFunction = async (functionID: number) => {
        if (!window.confirm("Slet denne funktion?")) return;
        await fetchJsonSafe(`${apiUrl}/api/function/${functionID}`, { method: "DELETE" });
        await fetchFunctions();
        setSelectedFunction(null);
    };

    const handleCopyFunctions = async (
        functionIds: number[],
        targetEventId: number
    ) => {
        if (!functionIds.length) {
            alert("Vælg mindst én funktion");
            return;
        }

        if (!targetEventId) {
            alert("Vælg target event");
            return;
        }

        await fetchJsonSafe(`${apiUrl}/api/function/copy-functions`, {
            method: "POST",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                functionIds,
                targetEventId,
            }),
        });

        await fetchFunctions();

        alert("Funktioner kopieret");
    };

    // -------------------- FILTERED DATA --------------------
    const filteredFunctions = selectedEventForFunctions
        ? functions.filter(f => Number(f.eventID) === Number(selectedEventForFunctions))
        : [];

    // -------------------- RENDER --------------------
    return (
        <div className="admin-page">
            <HeroSectionV2
                titleEn="Events"
                titleDa="Events"
                descriptionEn=""
                descriptionDa="Administrér events og funktioner"
                imageSrc={Billede1}
            />
            <AdminBackBar
                title="Events"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Events" } // current
                ]}
            />
            <div className="lists-grid">
                {/* EVENTS */}
                <div className="list-container">
                    <h2>Events</h2>
                    <button
                        className="add-btnnew"
                        onClick={() => setSelectedEvent({ eventName: "", active: true, isInternal: false, startTime: "", endTime: "" })}
                    >
                        + Opret Event
                    </button>

                    {selectedEvent && (
                        <EventForm
                            event={selectedEvent}
                            onSave={handleSaveEvent}
                            onCancel={() => setSelectedEvent(null)}
                        />
                    )}

                    <EventList
                        events={events}
                        onEdit={handleEditEvent}
                        onDelete={handleDeleteEvent}
                        onToggleStatus={handleSaveEvent}
                    />
                </div>

                {/* FUNCTIONS */}
                <div className="list-container2">
                    <h2>Functions</h2>
                    <select
                        className="styled-dropdown"
                        value={selectedEventForFunctions ?? ""}
                        onChange={e => {
                            setSelectedEventForFunctions(e.target.value ? Number(e.target.value) : null);
                            setSelectedFunction(null);
                        }}
                    >
                        <option value="">-- Vælg Event --</option>
                        {events.map(ev => (
                            <option key={ev.eventID} value={ev.eventID}>{ev.eventName}</option>
                        ))}
                    </select>

                    {selectedEventForFunctions && (
                        <>
                            <button
                                className="add-btnnew"
                                onClick={() =>
                                    setSelectedFunction({
                                        functionName: "",
                                        description: "",
                                        eventID: selectedEventForFunctions,
                                        functionCategoryId: undefined,
                                        useDesign2: false,
                                    })
                                }>
                                + Opret Funktion
                            </button>

                            {selectedFunction && (
                                <div
                                    className="function-modal-overlay"
                                    onMouseDown={(e) => {
                                        if (e.target === e.currentTarget) {
                                            setSelectedFunction(null);
                                        }
                                    }}
                                >
                                    <div className="function-modal">
                                        <FunctionForm
                                            func={selectedFunction}
                                            eventID={selectedEventForFunctions!}
                                            zones={zones}
                                            onSave={handleSaveFunction}
                                            categories={categories}
                                            onCancel={() => setSelectedFunction(null)}
                                        />
                                    </div>
                                </div>
                            )}

                            {filteredFunctions.length > 0 ? (
                                <FunctionList
                                    functions={filteredFunctions}
                                    events={events}
                                    onCopy={handleCopyFunctions}
                                    onEdit={async f => {
                                        const zones = await fetchJsonSafe(`${apiUrl}/api/zones/functions/byfunction/${f.functionID}`);
                                        setSelectedFunction({
                                            ...f,
                                            assignedZones: (zones || []).map(normalizeZone),
                                        });
                                    }}
                                    onDelete={handleDeleteFunction}
                                />
                            ) : (
                                <p>Ingen funktioner for dette event.</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminEventsPage;